import type { BoxProps } from '@hover/blueprint';
import { Body, Box } from '@hover/blueprint';
import React from 'react';

import { useFocusedView } from 'src/modules/renderings/hooks/useFocusedView';
import type { ImageLike } from 'src/modules/renderings/imageLike';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

const filledEllispeStyling = {
  width: '9px',
  height: '9px',
  backgroundColor: 'neutral.0',
};

const emptyEllipseStyling = {
  width: '7px',
  height: '7px',
};

const smallEllispeStyling = {
  width: '5px',
  height: '5px',
};

const Ellipse = (props: BoxProps) => (
  <Box
    backgroundColor="neutral.600"
    borderRadius="50%"
    display="inline-block"
    transitionDuration="350ms"
    transitionProperty="background-color"
    {...props}
  />
);

type CarouselOverlayProps = {
  images: ImageLike[];
  selectedIndex: number;
};

const CarouselOverlay = ({ images, selectedIndex }: CarouselOverlayProps) => {
  let dots;
  const selectedImage = images[selectedIndex];
  const imageCount = images.length;
  const t = useTranslate(TranslationNamespace.SHARED);
  const { isFocusedView } = useFocusedView();

  if (imageCount < 6) {
    dots = Array(imageCount)
      .fill('')
      .map((_, idx) => (
        <Ellipse
          // eslint-disable-next-line react/no-array-index-key
          key={`${idx}`}
          sx={
            idx === selectedIndex ? filledEllispeStyling : emptyEllipseStyling
          }
        />
      ));
  } else {
    dots = [
      <Ellipse key={0} sx={emptyEllipseStyling} />,
      <Ellipse key={1} sx={emptyEllipseStyling} />,
      <Ellipse key={2} sx={emptyEllipseStyling} />,
      <Ellipse key={3} sx={emptyEllipseStyling} />,
      <Ellipse key={4} sx={emptyEllipseStyling} />,
    ];

    // Set correct location to a filled ellipse
    if (selectedIndex < 2) {
      dots[selectedIndex] = <Ellipse key="<2" sx={filledEllispeStyling} />;
    } else if (imageCount - selectedIndex < 3) {
      dots[5 - (imageCount - selectedIndex)] = (
        <Ellipse key="<3" sx={filledEllispeStyling} />
      );
    } else {
      dots[2] = <Ellipse key={2} sx={filledEllispeStyling} />;
    }

    // Add swipe indicators if needed
    if (selectedIndex > 2) {
      dots.unshift(<Ellipse key="left" sx={smallEllispeStyling} />);
    }
    if (imageCount - selectedIndex > 3) {
      dots.push(<Ellipse key="right" sx={smallEllispeStyling} />);
    }
  }

  const label = selectedImage?.label(t);
  const showLabel = !!label;

  return (
    <>
      {isFocusedView && (
        <Box
          alignItems="center"
          backgroundColor="rgba(0,0,0,.65)"
          borderRadius="800"
          bottom="16px"
          display="flex"
          gap="3px"
          left="50%"
          overflow="hidden"
          paddingX={400}
          paddingY={200}
          position="absolute"
          transform="translate(-50%, 0)"
        >
          {dots}
        </Box>
      )}
      {showLabel && (
        <Box
          backgroundColor="rgba(0,0,0,.65)"
          borderRadius="200"
          left="50%"
          position="absolute"
          top={500}
          transform="translate(-50%, 0)"
        >
          <Body color="neutral.50" fontSize={100} marginX={400} marginY={0}>
            {selectedImage?.label(t)}
          </Body>
        </Box>
      )}
    </>
  );
};

export { CarouselOverlay };
